import { formatPaise } from "@luckymenu/core";
import { useTranslation } from "@luckymenu/i18n/react";
import { type PaymentClaim, usePaymentClaims } from "@luckymenu/staff-core";
import { Button } from "@luckymenu/ui-web";
import { useNavigate } from "@tanstack/react-router";
import { Smartphone } from "lucide-react";
import { useState } from "react";
import { useProblemText } from "./bill-parts";

/**
 * "Table 4 says they paid ₹540 by UPI" (P4-T22): one line per diner's "I
 * have paid", above every screen, for roles that record payments, until a
 * payment is recorded on that bill on any device. Confirm records the UPI
 * payment when it is exactly what is due; otherwise it opens the bill (not
 * billed yet) or the payment screen with UPI and the claimed amount filled
 * in. Hide takes it off this device. No sound: the diner is already paid
 * and waiting, unlike a new order (realtime-notifications.md).
 */
export function ClaimBanners() {
	const { t } = useTranslation("billing");
	const claims = usePaymentClaims();
	const navigate = useNavigate();
	const problemText = useProblemText();
	const [recorded, setRecorded] = useState(false);
	if (claims.claims.length === 0)
		return recorded ? (
			<p role="status" className="sr-only">
				{t("claim.recorded")}
			</p>
		) : null;

	const confirm = async (claim: PaymentClaim) => {
		setRecorded(false);
		const outcome = await claims.confirm(claim);
		if (outcome === "recorded") setRecorded(true);
		if (outcome === "bill")
			void navigate({ to: "/orders/$orderId/bill", params: { orderId: claim.orderId } });
		if (outcome === "payment")
			void navigate({
				to: "/orders/$orderId/pay",
				params: { orderId: claim.orderId },
				search: { method: "upi", amount: claim.amountPaise },
			});
	};

	return (
		<section aria-label={t("claim.label")} className="mb-3 grid gap-2">
			{claims.claims.map((claim) => (
				<div
					key={claim.key}
					className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-primary/50 bg-primary/10 px-3 py-2 text-sm"
				>
					<span className="flex min-w-0 flex-1 basis-56 items-start gap-2">
						<Smartphone className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
						<span className="grid">
							<span role="status" className="font-medium">
								{claim.tableLabel
									? t("claim.table", {
											table: claim.tableLabel,
											amount: formatPaise(claim.amountPaise),
										})
									: t("claim.takeaway", {
											number: claim.orderNo,
											amount: formatPaise(claim.amountPaise),
										})}
							</span>
							<span className="text-xs text-muted-foreground">{t("claim.check")}</span>
							{claims.problem?.key === claim.key ? (
								<span role="alert" className="text-xs text-destructive">
									{problemText(claims.problem)}
								</span>
							) : null}
						</span>
					</span>
					<span className="flex shrink-0 gap-2">
						<Button variant="ghost" onClick={() => claims.hide(claim)}>
							{t("claim.hide")}
						</Button>
						<Button disabled={claims.busy === claim.key} onClick={() => void confirm(claim)}>
							{claims.busy === claim.key ? t("claim.confirming") : t("claim.confirm")}
						</Button>
					</span>
				</div>
			))}
		</section>
	);
}
